import { CircularProgress, Grid } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ButtonComponent from "../../components/ButtonComponent";
import * as types from "../../store/types/triagem";
import {
  EntarTriagemAction,
  EntrarAtendimentoAction,
  StatusPausaTriagem,
} from "../../store/actions/triagem";
import SwitchIOS from "./switchOnEOff";
import http from "../../services/api";
import formatName from "../../helpers/formatName";
import SwitchPausaratendimento from "./switchPausarAtendimento";
import calcularTempoEmEspera from "./calcularTempoEspera";
import usePageVisibility from "../../helpers/pageVisible";

// ─── Componente auxiliar de linha de info ────────────────────────────────────
function InfoRow({ label, value, valueColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: 12, color: "#6b7280", fontFamily: "Poppins, sans-serif" }}>
        {label}
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: valueColor ?? "#111827",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── HomeScreen ───────────────────────────────────────────────────────────────
function HomeScreen() {
  const dispatch = useDispatch();
  const isVisible = usePageVisibility();
  const { atendimentosAbertos } = useSelector((state) => state.triagem);
  const navigate = useNavigate();
  const { permissoes } = useSelector((state) => state.usuario);
  const { horariosAtendimentoTriagem } = useSelector((state) => state.triagem);
  const [loading, setLoading] = useState("");

  // FIX: referência persistente ao objeto de áudio — sem ref, o pause() não funciona
  // porque o objeto criado no play é diferente do que você tentaria pausar depois
  const ringtoneRef = useRef(null);

  // ── Para o ringtone e reseta o estado Redux de som ──────────────────────────
  function stopRingtone() {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current = null;
    }
    dispatch({ type: types.SET_PLAY_SONG, payload: false });
  }

  // ── Entra na chamada (triagem ou atendimento) ───────────────────────────────
  async function EntarChamadaTriagem(data) {
    // FIX: para o som ANTES de navegar, independente de qual chamada foi aceita
    stopRingtone();
    setLoading(data);
    try {
      if (permissoes.tipo === "TRIAGEM") {
        await EntarTriagemAction({ sala: data.sala }).then((res) => {
          navigate(`/app/home/${res.sala}`, { replace: true });
          setLoading("");
        });
      } else if (permissoes.tipo === "ATENDIMENTO") {
        await EntrarAtendimentoAction({ sala: data.sala }).then((res) => {
          navigate(`/app/home/atendimento/${res.sala}`, { replace: true });
          setLoading("");
        });
      } else {
        throw setLoading("");
      }
    } catch (e) {
      console.log(e);
      setLoading("");
    }
  }

  // ── Atualiza status operacional no banco ────────────────────────────────────
  async function updateStatusInDatabase(newStatus) {
    await http
      .put("/api/v1/atendimento/atualizar-status", { statusOperacional: newStatus })
      .then((res) => console.log(res.data))
      .catch((e) => console.log(e));
  }

  // ── Pausa / retoma triagem ──────────────────────────────────────────────────
  async function UpdateStatusPausaAtendimento(newStatus) {
    await http
      .put("/api/v1/atendimento/horarios/pausar", {
        validation: newStatus,
        modalidade_atendimento: localStorage.getItem("modalidade"),
      })
      .then(() => StatusPausaTriagem(localStorage.getItem("modalidade")))
      .catch((e) => console.log(e));
  }

  // ── Status inicial da pausa ─────────────────────────────────────────────────
  useEffect(() => {
    StatusPausaTriagem(localStorage.getItem("modalidade"));
  }, []);

  // ── Quando a aba volta a ficar visível, para o som ──────────────────────────
  useEffect(() => {
    if (isVisible) {
      stopRingtone();
    }
  }, [isVisible]);

  // ── FIX: garante que o áudio é liberado se o componente desmontar ───────────
  useEffect(() => {
    return () => stopRingtone();
  }, []);

  const totalEspera = atendimentosAbertos?.length ?? 0;

  return (
    <div style={{ padding: "0 4px" }}>
      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 28,
          paddingBottom: 20,
          borderBottom: "1px solid #f0f0f0",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              color: "#111827",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            Fila de Atendimento
          </h1>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 13,
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {totalEspera === 0
              ? "Nenhum paciente aguardando"
              : `${totalEspera} paciente${totalEspera > 1 ? "s" : ""} aguardando atendimento`}
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {permissoes.tipo === "ATENDIMENTO" && (
            <SwitchIOS onUpdateStatus={updateStatusInDatabase} data={permissoes} />
          )}
          {permissoes.tipo === "TRIAGEM" && (
            <SwitchPausaratendimento
              onUpdateStatus={UpdateStatusPausaAtendimento}
              data={horariosAtendimentoTriagem}
            />
          )}
        </div>
      </div>

      {/* ── Estado vazio ───────────────────────────────────────────────────── */}
      {totalEspera === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "80px 20px",
            color: "#9ca3af",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
          <p style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Fila vazia</p>
          <p style={{ fontSize: 13, marginTop: 6, color: "#b0b7c3" }}>
            Nenhum paciente aguardando no momento
          </p>
        </div>
      ) : (
        /* ── Grade de cards ──────────────────────────────────────────────── */
        <Grid container spacing={2}>
          {atendimentosAbertos?.map((at, index) => {
            const isLoadingThis = at.sala === loading.sala;
            const tempoEspera = calcularTempoEmEspera(at?.data_inicio);
            // Destaque visual se paciente está esperando há mais de 1 hora
            const isUrgent = tempoEspera.includes("hora");
            const inicial = at?.nome?.charAt(0)?.toUpperCase() ?? "?";

            return (
              // FIX: key corrigido — index.id não existe (index é número), usa at.sala
              <Grid item xs={12} sm={6} md={4} key={at.sala ?? index}>
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: 12,
                    border: isUrgent ? "1.5px solid #fca5a5" : "1px solid #e5e7eb",
                    padding: 20,
                    boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
                    position: "relative",
                    overflow: "hidden",
                    fontFamily: "Poppins, sans-serif",
                    transition: "box-shadow 0.2s",
                  }}
                >
                  {/* Badge longa espera */}
                  {isUrgent && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        background: "#ef4444",
                        color: "#fff",
                        fontSize: 9,
                        fontWeight: 700,
                        padding: "3px 10px",
                        borderBottomLeftRadius: 8,
                        letterSpacing: "0.06em",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    >
                      LONGA ESPERA
                    </div>
                  )}

                  {/* Avatar + nome */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 14,
                    }}
                  >
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        background: "#eff6ff",
                        color: "#3b82f6",
                        fontWeight: 700,
                        fontSize: 17,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontFamily: "Poppins, sans-serif",
                      }}
                    >
                      {inicial}
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: "#111827",
                          lineHeight: 1.3,
                        }}
                      >
                        {at?.nome}
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                        {formatName(at?.parceiro_integracao_nome)}
                      </div>
                    </div>
                  </div>

                  {/* Detalhes */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 7,
                      marginBottom: 16,
                      padding: 12,
                      background: "#f9fafb",
                      borderRadius: 8,
                    }}
                  >
                    <InfoRow label="Modalidade" value={at?.modalidade} />
                    <InfoRow
                      label="Aguardando"
                      value={tempoEspera}
                      valueColor={isUrgent ? "#ef4444" : "#f59e0b"}
                    />
                  </div>

                  {/* Ações */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {isLoadingThis ? (
                      <div
                        style={{
                          flex: 1,
                          background: "#16a34a",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          padding: "10px 16px",
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 500,
                          fontFamily: "Poppins, sans-serif",
                        }}
                      >
                        <CircularProgress style={{ color: "#fff", width: 14, height: 14 }} />
                        Iniciando...
                      </div>
                    ) : (
                      <button
                        onClick={() => EntarChamadaTriagem(at)}
                        style={{
                          flex: 1,
                          background: "#111827",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          padding: "10px 16px",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "Poppins, sans-serif",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#374151")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#111827")}
                      >
                        Entrar na chamada
                      </button>
                    )}

                    {/* Silenciar — agora chama stopRingtone que realmente para o áudio */}
                    <button
                      onClick={stopRingtone}
                      title="Silenciar chamada"
                      style={{
                        background: "#f3f4f6",
                        border: "none",
                        borderRadius: 8,
                        padding: "10px 12px",
                        cursor: "pointer",
                        fontSize: 16,
                        lineHeight: 1,
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#e5e7eb")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                    >
                      🔕
                    </button>
                  </div>
                </div>
              </Grid>
            );
          })}
        </Grid>
      )}
    </div>
  );
}

export default HomeScreen;